import type { ReactNode } from 'react';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import SelectableQuantityCounter from '../../../../components/ui/SelectableQuantityCounter/SelectableQuantityCounter';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryOptionGoodsForm } from '../../types';
import styles from './OptionNamedSelectableStepNamedCollectionList.module.scss';

type OptionNamedSelectableStepNamedCollectionListProps = {
  formItem: LotteryOptionGoodsForm;
  namedSelectableItems: NonNullable<LotteryOptionGoodsForm['optionInfo']['namedSelectable']>;
  isDeadlinePassed: boolean;
  editMode: boolean;
  selectedItems: Map<string, number>;
  inputErrors: Record<string, boolean>;
  onInputChange: (inputKey: string, value: string) => void;
  getNamedInputPlaceholder: () => string;
  invalidCharacterErrorMessage: string;
  staging: (formItem: LotteryOptionGoodsForm) => ReactNode;
};

function OptionNamedSelectableStepNamedCollectionList({
  formItem,
  namedSelectableItems,
  isDeadlinePassed,
  editMode,
  selectedItems,
  inputErrors,
  onInputChange,
  getNamedInputPlaceholder,
  invalidCharacterErrorMessage,
  staging,
}: OptionNamedSelectableStepNamedCollectionListProps) {
  const renderNamedSelectableNameItem = (
    namedSelectableItem: NonNullable<LotteryOptionGoodsForm['optionInfo']['namedSelectable']>[number],
    index: number,
  ) => {
    // 各景品の選択数量を取得
    const goodId = `${formItem.id}-${index}`;
    const selectedQuantity = selectedItems.get(goodId) || 0;

    // 選択数量が0の場合、かつ、入力期限を迎えていない場合は処理を走らせても意味がないので表示しない
    // 入力期限を迎えたら、selectedQuantityがずっと0のままだが、後続の処理を無条件に走らせる必要があるため
    if (selectedQuantity === 0 && !isDeadlinePassed) {
      return null;
    }

    const autoInputCount = namedSelectableItem.notes?.filter((item) => item.isSystemInput).length || 0;

    const isAutoInput = () => {
      // 入力期限を迎えていない場合は非表示
      if (!isDeadlinePassed) return false;

      // 自動入力数が0の場合は非表示
      if (autoInputCount === 0) return false;

      // notesが存在しない場合は非表示
      const notes = namedSelectableItem.notes;
      if (!notes) return false;

      // notesが存在して、システムインプットされている場合は表示、それ以外は非表示
      return notes.some((item) => item.isSystemInput);
    };

    return (
      <div key={`named_selectable-${formItem.id}-${index}`} className={`${styles.itemCardNamed}`}>
        <div className={styles.itemCardNamedInner}>
          <div className={`${styles.itemDetailNamedWrapper} ${utilsStyles.mtXSmall}`}>
            <div className={styles.itemImage}>
              <ImageView alt={namedSelectableItem.label || formItem.name} src={namedSelectableItem.imageUrl || formItem.imageUrl} />
            </div>
            <div className={`${styles.itemDetailNamed} ${utilsStyles.mlSmall}`}>
              <div className={styles.stagingRankWrapper}>{staging(formItem)}</div>
              <div className={styles.itemNameWrapper}>
                <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.name}</p>
                <p className={`${styles.itemName} ${utilsStyles.textBold}`}>
                  {namedSelectableItem.label || 'オプション入力・選択項目'}
                </p>
              </div>

              <div className={styles.desiredQuantityWrapper}>
                {isAutoInput() ? (
                  <div className={styles.desiredQuantityAutoInputLabel}>
                    自動入力数：
                    {autoInputCount}個
                  </div>
                ) : (
                  <p className={`${styles.desiredQuantityLabel} ${utilsStyles.textBold}`}>希望する個数</p>
                )}
                <div className={styles.desiredQuantityCounter}>
                  {isDeadlinePassed ? (
                    // 期限切れ後はサーバーから取得した個数をそのまま表示する
                    <SelectableQuantityCounter
                      goodId={`${formItem.id}-${index}`}
                      acquiredCount={formItem.totalAcquiredCount}
                      // 選べる＆宛名オプションの宛名UIでもサーバーから取得した数量をカウンターとして表示する必要がある。
                      // ただし、カウンターの数値は常に変更できないようにする必要がある。
                      // そのため、強制的に無効にすることでカウンターの数値を変更できないようにできる。
                      disabled={true}
                      tradeableInCount={formItem.totalAcquiredCount}
                      totalSelectedInLabel={Array.from(selectedItems.values()).reduce((sum, current) => sum + current, 0)}
                      initialQuantity={namedSelectableItem.value ? Number(namedSelectableItem.value) : 0}
                      isPastDeadline={isDeadlinePassed}
                      isAutoInput={isAutoInput()}
                    />
                  ) : (
                    // 期限切れ前はquantityステップで選択した個数をそのまま表示する
                    <SelectableQuantityCounter
                      goodId={`${formItem.id}-${index}`}
                      acquiredCount={selectedQuantity}
                      // 選べる＆宛名オプションの宛名UIでも選べるUIで選択した数量をカウンターとして表示する必要がある。
                      // ただし、カウンターの数値は常に変更できないようにする必要がある。
                      // そのため、強制的に無効にすることでカウンターの数値を変更できないようにできる。
                      disabled={true}
                      tradeableInCount={selectedQuantity}
                      totalSelectedInLabel={Array.from(selectedItems.values()).reduce((sum, current) => sum + current, 0)}
                      initialQuantity={selectedQuantity ? Number(selectedQuantity) : 0}
                      isPastDeadline={isDeadlinePassed}
                      isAutoInput={isAutoInput()}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.optionInputNamedWrapper}>
            {selectedQuantity > 1 && (
              <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                ※同一の内容をご希望の場合も、
                <span className={`${utilsStyles.primaryColor} ${utilsStyles.textBold}`}>入力欄１個につき１点</span>
                の入力をお願いいたします
              </p>
            )}
            {Array.from({ length: selectedQuantity }, (_, inputIndex) => {
              // 既存の入力内容を取得（namedSelectableのnotesから）
              const noteItem = namedSelectableItem.notes?.[inputIndex];
              const existingValue = noteItem?.value || '';
              const isSystemInput = noteItem?.isSystemInput || false;
              const inputKey = `named_selectable-${formItem.id}-${index}-${inputIndex}`;
              const hasError = inputErrors[inputKey];
              const displayValue =
                isDeadlinePassed && isSystemInput && existingValue ? `【自動入力】${existingValue}` : existingValue;
              const optionInputTitle = `オプション入力欄（${inputIndex + 1}/${selectedQuantity}）`;

              return (
                <div key={`${formItem.id}-${index}-${inputIndex}`} className={styles.inputContainer}>
                  <div className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                    {optionInputTitle}
                  </div>
                  {hasError && <div className={styles.errorMessage}>{invalidCharacterErrorMessage}</div>}
                  <textarea
                    key={`input-${formItem.id}-${index}-${inputIndex}-${existingValue}`}
                    name={`optionInput-${formItem.id}-${index}-${inputIndex}`}
                    placeholder={getNamedInputPlaceholder()}
                    className={`${styles.optionInput} ${editMode && isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                    defaultValue={displayValue}
                    onChange={(e) => onInputChange(inputKey, e.target.value)}
                    disabled={editMode && isDeadlinePassed}
                    rows={3}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return <>{namedSelectableItems.map(renderNamedSelectableNameItem)}</>;
}

export default OptionNamedSelectableStepNamedCollectionList;
